import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CalendarToday, Tag, Category } from '@mui/icons-material';

const PostSidebar = ({
  categories = [],
  tags = [],
  recentPosts = [],
  featuredPosts = [],
  loading = false
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lng = i18n.language;

  const handleCategoryClick = (category) => {
    navigate(`/blog?category=${category.slug}`);
  };

  const handleTagClick = (tag) => {
    navigate(`/blog?tag=${tag.slug}`);
  };

  const handlePostClick = (post) => {
    navigate(`/blog/${post.slug}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString(lng === 'ar' ? 'ar-KW' : 'en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {t('blog.featuredPosts', 'Featured Posts')}
            </Typography>
            <List dense>
              {featuredPosts.slice(0, 3).map((post, index) => (
                <React.Fragment key={post.id}>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handlePostClick(post)} sx={{ py: 1 }}>
                      <ListItemText
                        primary={post.title?.[lng] || post.title?.en}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: {
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }
                        }}
                        secondary={formatDate(post.published_at)}
                        secondaryTypographyProps={{
                          variant: 'caption',
                          color: 'text.secondary'
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < featuredPosts.slice(0, 3).length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Categories */}
      {categories.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Category color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('blog.categories', 'Categories')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {categories.map((category) => (
                <Box
                  key={category.id}
                  onClick={() => handleCategoryClick(category)}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {category.name?.[lng] || category.name?.en}
                  </Typography>
                  <Chip
                    label={category.posts_count || 0}
                    size="small"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Recent Posts */}
      {recentPosts.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CalendarToday color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('blog.recentPosts', 'Recent Posts')}
              </Typography>
            </Box>
            <List dense>
              {recentPosts.slice(0, 5).map((post, index) => (
                <React.Fragment key={post.id}>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handlePostClick(post)} sx={{ py: 1 }}>
                      <ListItemText
                        primary={post.title?.[lng] || post.title?.en}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: {
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }
                        }}
                        secondary={formatDate(post.published_at)}
                        secondaryTypographyProps={{
                          variant: 'caption',
                          color: 'text.secondary'
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < recentPosts.slice(0, 5).length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Tags */}
      {tags.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Tag color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('blog.tags', 'Tags')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {tags.slice(0, 15).map((tag) => (
                <Chip
                  key={tag.id}
                  label={`${tag.name?.[lng] || tag.name?.en} (${tag.posts_count || 0})`}
                  size="small"
                  variant="outlined"
                  onClick={() => handleTagClick(tag)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    height: 24,
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText',
                      borderColor: 'primary.main',
                    },
                  }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PostSidebar;
